import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";

// Static Tailwind class mapping (kept as literal strings so JIT picks them up).
export const WORKFLOW_COLOR_STYLES = {
  slate:   { dot: "bg-slate-400",   bg: "bg-slate-100 text-slate-700 border-slate-200" },
  sky:     { dot: "bg-sky-500",     bg: "bg-sky-50 text-sky-700 border-sky-200" },
  amber:   { dot: "bg-amber-500",   bg: "bg-amber-50 text-amber-700 border-amber-200" },
  emerald: { dot: "bg-emerald-500", bg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rose:    { dot: "bg-rose-500",    bg: "bg-rose-50 text-rose-700 border-rose-200" },
  blue:    { dot: "bg-blue-500",    bg: "bg-blue-50 text-blue-700 border-blue-200" },
  orange:  { dot: "bg-orange-500",  bg: "bg-orange-50 text-orange-700 border-orange-200" },
  violet:  { dot: "bg-violet-500",  bg: "bg-violet-50 text-violet-700 border-violet-200" },
  zinc:    { dot: "bg-zinc-500",    bg: "bg-zinc-100 text-zinc-700 border-zinc-200" },
};
export const WORKFLOW_COLORS = Object.keys(WORKFLOW_COLOR_STYLES);

export const workflowColorStyle = (color) =>
  WORKFLOW_COLOR_STYLES[color] || WORKFLOW_COLOR_STYLES.slate;

/**
 * Hook: fetch active workflow statuses from the API.
 * Returns { statuses, byKey, reload, loading }.
 * `byKey(key)` returns the status document or a graceful fallback.
 */
export function useWorkflowStatuses(includeInactive = false) {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/processes/workflow/statuses",
        { params: includeInactive ? { include_inactive: true } : {} });
      setStatuses(data.items || []);
    } catch {
      // AP-106 fix · Portal client tokens don't have permission to hit this
      // admin endpoint. Swallow the 401/403 to avoid triggering CRA's
      // unhandled-rejection overlay. Fallback to empty statuses; byKey()
      // already returns a graceful default.
      setStatuses([]);
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => { reload(); }, [reload]);

  const byKey = (key) => {
    const found = statuses.find((s) => s.key === key);
    if (found) return found;
    return { key, label: key || "—", color: "slate", auto_progress_pct: null, is_active: true };
  };

  return { statuses, byKey, reload, loading };
}
